/**
 * Booking Slots - logique partagée de créneaux de réservation
 *
 * Centralise la classification des jours réservés (full / partial) et le calcul
 * du premier créneau libre, utilisés à la fois par flatpickr-init-app.js (pickers
 * du formulaire) et shared/_fullCalendar.js (coloration du calendrier lecture seule).
 *
 * Règle métier : un jour n'est "complet" (non sélectionnable) que si les réservations
 * existantes couvrent toute la fenêtre d'ouverture. Sinon il reste sélectionnable et
 * le contrôle de chevauchement horaire (strict, demi-ouvert) fait foi.
 */

// Fenêtre d'ouverture journalière (AJUSTABLE selon les horaires de la commune).
// Sert à définir "jour complet" (= la fenêtre est entièrement occupée) et le premier
// créneau libre. 23:00 correspond à l'heure de fin par défaut du formulaire : une
// réservation 08:00–23:00 occupe donc toute la journée et bloque le jour.
export const BOOKABLE_DAY_START = '08:00';
export const BOOKABLE_DAY_END = '23:00';

const DAY_START_MIN = timeToMinutes(BOOKABLE_DAY_START);
const DAY_END_MIN = timeToMinutes(BOOKABLE_DAY_END);

/**
 * Convertit "HH:mm" en minutes depuis minuit. "24:00" -> 1440.
 */
export function timeToMinutes(time) {
    if (!time || typeof time !== 'string') return 0;
    const parts = time.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
}

/**
 * Convertit des minutes depuis minuit en "HH:mm" (1440 -> "24:00").
 */
export function minutesToTime(mins) {
    // Borne sur une journée complète (0..1440). La couverture de fenêtre utilise
    // DAY_END_MIN séparément ; ici on préserve les heures réelles (ex. 23:30).
    const clamped = Math.max(0, Math.min(24 * 60, mins));
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

/**
 * Clé de jour locale "YYYY-MM-DD" (évite le décalage d'un jour de toISOString()).
 */
export function localDateKey(date) {
    return (
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0')
    );
}

/**
 * Fusionne une liste d'intervalles {startMin,endMin} chevauchants/adjacents.
 * Retourne une liste triée d'intervalles disjoints.
 */
function mergeIntervals(intervals) {
    if (!intervals.length) return [];
    const sorted = intervals
        .map((i) => ({ startMin: i.startMin, endMin: i.endMin }))
        .sort((a, b) => a.startMin - b.startMin);
    const merged = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
        const last = merged[merged.length - 1];
        const curr = sorted[i];
        if (curr.startMin <= last.endMin) {
            last.endMin = Math.max(last.endMin, curr.endMin);
        } else {
            merged.push(curr);
        }
    }
    return merged;
}

/**
 * Indique si des intervalles couvrent toute la fenêtre d'ouverture.
 */
export function intervalsCoverWindow(intervals) {
    const merged = mergeIntervals(intervals);
    return merged.some(
        (iv) => iv.startMin <= DAY_START_MIN && iv.endMin >= DAY_END_MIN
    );
}

/**
 * Construit {full, partial} à partir de la réponse API (liste de réservations
 * {start, end} ISO). Remplace l'ancienne heuristique coversFullDay (>=8h / 08-22).
 *
 * - full    : ["YYYY-MM-DD", ...] jours saturés (aucun créneau libre)
 * - partial : [{date, startTime, endTime}, ...] créneaux occupés (jours encore libres)
 *
 * La forme {full, partial} reste compatible avec checkDateRangeOverlap.
 */
export function buildBookedDays(apiData) {
    // intervalsByDay["YYYY-MM-DD"] = [{startMin, endMin}, ...]
    const intervalsByDay = {};

    const addInterval = (dateKey, startMin, endMin) => {
        if (endMin <= startMin) return;
        if (!intervalsByDay[dateKey]) intervalsByDay[dateKey] = [];
        intervalsByDay[dateKey].push({ startMin, endMin });
    };

    if (Array.isArray(apiData)) {
        apiData.forEach((rent) => {
            if (!rent.start || !rent.end) return;

            const eventStart = new Date(rent.start);
            const eventEnd = new Date(rent.end);
            if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) return;

            // Heures locales de début/fin (clé de jour locale -> pas de décalage TZ)
            const startTime = rent.start.split('T')[1]?.slice(0, 5) || '00:00';
            const endTime = rent.end.split('T')[1]?.slice(0, 5) || '23:59';
            const startKey = localDateKey(eventStart);
            const endKey = localDateKey(eventEnd);

            if (startKey === endKey) {
                addInterval(startKey, timeToMinutes(startTime), timeToMinutes(endTime));
            } else {
                // Multi-jours : 1er jour start->fin fenêtre, jours intérieurs pleins,
                // dernier jour début fenêtre->end.
                let d = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
                const dEnd = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
                while (d <= dEnd) {
                    const key = localDateKey(d);
                    if (key === startKey) {
                        addInterval(key, timeToMinutes(startTime), DAY_END_MIN);
                    } else if (key === endKey) {
                        addInterval(key, DAY_START_MIN, timeToMinutes(endTime));
                    } else {
                        addInterval(key, DAY_START_MIN, DAY_END_MIN);
                    }
                    d.setDate(d.getDate() + 1);
                }
            }
        });
    }

    const full = [];
    const partial = [];

    Object.keys(intervalsByDay).forEach((dateKey) => {
        const merged = mergeIntervals(intervalsByDay[dateKey]);
        if (intervalsCoverWindow(merged)) {
            full.push(dateKey);
        } else {
            merged.forEach((iv) => {
                // "24:00" n'est pas parsable par new Date(...Txx:00:00) côté
                // checkDateRangeOverlap -> on le borne à "23:59". Les heures réelles
                // (ex. 23:30) sont conservées.
                const endStr =
                    iv.endMin >= 24 * 60 ? '23:59' : minutesToTime(iv.endMin);
                partial.push({
                    date: dateKey,
                    startTime: minutesToTime(iv.startMin),
                    endTime: endStr,
                });
            });
        }
    });

    // Dédoublonnage + filtrage des jours passés (cohérent avec l'ancien code)
    const uniqueFull = [...new Set(full)];
    const today = new Date();
    const todayStr = localDateKey(today);
    const uniquePartial = partial
        .filter((p) => !uniqueFull.includes(p.date))
        .filter((p) => p.date >= todayStr);

    return { full: uniqueFull, partial: uniquePartial };
}

/**
 * Retourne le premier créneau libre {startTime, endTime} d'un jour donné, à partir
 * du début de la fenêtre d'ouverture, borné par la réservation suivante ou la fin de
 * fenêtre. Si aucune réservation ce jour-là -> défauts 08:00 / 23:00.
 *
 * @param {string} dateStr - "YYYY-MM-DD"
 * @param {{full:string[], partial:{date,startTime,endTime}[]}} booked
 */
export function getFirstFreeSlot(dateStr, booked) {
    const DEFAULT = { startTime: '08:00', endTime: '23:00' };
    if (!booked || !Array.isArray(booked.partial)) return DEFAULT;

    const dayIntervals = mergeIntervals(
        booked.partial
            .filter((p) => p.date === dateStr)
            .map((p) => ({
                startMin: timeToMinutes(p.startTime),
                endMin: timeToMinutes(p.endTime),
            }))
    );

    if (!dayIntervals.length) return DEFAULT;

    // Cherche le premier trou >= début fenêtre.
    let cursor = DAY_START_MIN;
    for (const iv of dayIntervals) {
        if (iv.startMin > cursor) {
            // Trou libre [cursor, iv.startMin]
            return { startTime: minutesToTime(cursor), endTime: minutesToTime(iv.startMin) };
        }
        cursor = Math.max(cursor, iv.endMin);
    }

    // Pas de trou avant la dernière résa : créneau après la dernière, jusqu'à la fin
    // de la fenêtre d'ouverture.
    if (cursor < DAY_END_MIN) {
        return { startTime: minutesToTime(cursor), endTime: minutesToTime(DAY_END_MIN) };
    }

    // Jour saturé (ne devrait pas arriver : il serait classé dans `full`).
    return DEFAULT;
}

/**
 * Parse une chaîne "YYYY-MM-DDTHH:mm:00" ou "DD-MM-YYYYTHH:mm:00" en Date locale.
 * Retourne null si invalide (ex. "...T24:00:00"). Migré depuis flatpickr-init-app.js
 * pour rendre le contrôle de chevauchement testable au même endroit.
 */
export function parseDateTimezone(dateStr) {
    if (!dateStr || dateStr.trim() === '') return null;

    if (dateStr.includes('T')) {
        const parts = dateStr.split('T');
        let datePart = parts[0];
        const timePart = parts[1];
        const dateSegments = datePart.split('-');
        if (dateSegments.length === 3 && dateSegments[0].length <= 2) {
            // Format DD-MM-YYYY -> YYYY-MM-DD
            datePart = dateSegments[2] + '-' + dateSegments[1] + '-' + dateSegments[0];
        }
        const date = new Date(datePart + 'T' + timePart);
        if (isNaN(date.getTime())) return null;
        return date;
    }

    return null;
}

/**
 * Format DD-MM-YYYY (affichage des messages de conflit).
 */
function formatDMY(date) {
    return (
        String(date.getDate()).padStart(2, '0') +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        date.getFullYear()
    );
}

/**
 * Renvoie la liste des conflits entre la plage [startDate, endDate] proposée et les
 * réservations existantes `booked` ({full, partial}). Chaque entrée :
 *   { type: 'full', date }  ou  { type: 'partial', date, startTime, endTime }
 * Test d'intervalle demi-ouvert (strict) : le dos-à-dos (fin == début) est autorisé.
 * Migré depuis flatpickr-init-app.js (logique inchangée).
 */
export function checkDateRangeOverlap(startDate, endDate, booked) {
    const overlapDates = [];

    // Itération sur des JOURS normalisés à minuit, et non sur l'horodatage de début.
    // `new Date(startDate)` conservait l'HEURE de départ pendant toute la boucle
    // (seul `setDate(+1)` avançait) : dès que l'heure de fin tombait plus tôt dans
    // la journée que l'heure de début — un démontage le 14/06 à 12:00 après un
    // montage le 12/06 à 14:00, ou un événement qui se termine à 02:00 le lendemain —
    // le dernier tour dépassait `endDate` et la boucle s'arrêtait UN JOUR TROP TÔT.
    // Le jour de fin n'était donc jamais confronté aux réservations existantes.
    // Le test horaire ci-dessous continue de porter sur `startDate`/`endDate` réels.
    const current = new Date(
        startDate.getFullYear(), startDate.getMonth(), startDate.getDate()
    );
    const dernierJour = new Date(
        endDate.getFullYear(), endDate.getMonth(), endDate.getDate()
    );

    while (current <= dernierJour) {
        const dateStr =
            current.getFullYear() +
            '-' +
            String(current.getMonth() + 1).padStart(2, '0') +
            '-' +
            String(current.getDate()).padStart(2, '0');
        const formattedDate = formatDMY(current);
        if (booked.full.includes(dateStr)) {
            overlapDates.push({ type: 'full', date: formattedDate });
        } else {
            const partialObj = booked.partial.filter((p) => p.date === dateStr);
            for (let i = 0; i < partialObj.length; i++) {
                const startTime = startDate.getTime();
                const endTime = endDate.getTime();
                const ps = parseDateTimezone(
                    partialObj[i].date + 'T' + partialObj[i].startTime + ':00'
                );
                const pe = parseDateTimezone(
                    partialObj[i].date + 'T' + partialObj[i].endTime + ':00'
                );
                if (!ps || !pe) continue;
                if (startTime < pe.getTime() && endTime > ps.getTime()) {
                    overlapDates.push({
                        type: 'partial',
                        date: formattedDate,
                        startTime: partialObj[i].startTime,
                        endTime: partialObj[i].endTime,
                    });
                }
            }
        }
        current.setDate(current.getDate() + 1);
    }
    return overlapDates;
}
